/**
 * Raid select ??rulesFor / gambit assembly / computeAiIntent.
 * Phaser-free. Owns behavior tiers; delegates geometry to motion/engage.
 */
import {
  raidCatalog,
  raidGambitRulesFor,
  type RaidGambitRow,
} from "../../data/raid-catalog";
import type { RaidClassId } from "../../data/raid-catalog";
import {
  dispositionKnob,
  instinctKnob,
  commitSecForBond,
  dispositionOf,
  instinctOf,
} from "./raid-disposition";
import {
  selectBehavior,
  underPressure,
  dashArrivedOrHardBlocked,
  type BehaviorRule,
} from "../../sim";
import type { Order } from "../../sim";
import {
  classLoadout,
  decideFromKit,
  isBehaviorKitId,
  kitIdForChip,
  scoreFromKit,
  defaultKitPorts,
} from "./raid-kit-interp";
import { topThreatTarget } from "../../sim";
import type {
  RaidBoss,
  RaidOrderKind,
  RaidUnit,
  RaidUnitOrderKind,
} from "./raid-types";
import { raidAggroSticky } from "./raid-aggro";
import {
  collectRaidDangerZones,
  hasDangerTier,
  buildLocalHostileVolumes,
  raidSurviveThreatSafe,
  unitInDangerZones,
  pointClearsAllDangerZones,
  projectClearOfDangerZones,
  clearOfPackChewAttack,
  chargeCorridorActive,
  pointInDangerZone,
} from "./raid-threat";
import { planAdjutant, slotFor } from "./raid-adjutant";
import { raidFreezeDiag } from "./raid-freeze-diag";
import { sampleThreatAt } from "../../sim";
import {
  bondOverCompliancePull01,
  healerWireOn,
  instinctAbandonFireWhileDodge,
  instinctAllowsAoEDodge,
  resolveSoftSafeFocusId,
  resolveSoftSafeHealNeed,
  softAaHealBelowPct,
  encounterPackChargeForesightActive,
  unitHasRoleTag,
} from "./judgment";
import {
  type AiIntent,
  clearUnitOrder,
  classKnobs,
  dist,
  pickEngageTarget,
  stickyEngageTarget,
  hostileAttackReach,
  addAttackReach,
  resolveAddRadius,
  hostileHardMinSep,
  faceTarget,
  faceBoss,
  facePoint,
  engagementStyle,
  fleeFromAdds,
  pickInWeaponReachAdd,
} from "./raid-engage";
import {
  resolveGoalInBounds,
  formationReference,
  ringPointFromUnit,
  clampGoalToAddReach,
  applySlot,
  applySlotNearHostile,
  allyInBrass,
  lowestAlly,
  pickInRangeHealNeed,
  frontTank,
  dodgeGoalInBounds,
  dodgeIntent,
  finalizeAllyMoveGoal,
  retreatToAllyIntent,
  spaceOutBossGoal,
  retreatToHealerGoal,
  pickHealerSupportGoal,
} from "./raid-motion";
import type { SpawnHeatState } from "./raid-spawn-heat";

// ???? behavior rules (tiers by array order) ????????????????????????????????????????????????????????????????????

type BrainCtx = {
  u: RaidUnit;
  boss: RaidBoss;
  allies: readonly RaidUnit[];
  enemies: readonly RaidUnit[];
  focusBoss: boolean;
  time: number;
  /** When false, engage never aims the dormant boss. */
  includeBoss: boolean;
  /** Active commander tactic order payload (regroup/retreat/hold). */
  order: Order<RaidOrderKind> | null;
  spawnHeat?: SpawnHeatState | null;
};

type RaidRule = BehaviorRule<BrainCtx, AiIntent>;

const combatTable = () => raidCatalog.balance.combat;

/** Boss proximity used by gambit `bossClose` and soft spatial awareness. */
function isBossCloseCtx(c: BrainCtx): boolean {
  if (unitHasRoleTag(c.u, "tank") || unitHasRoleTag(c.u, "frontline")) {
    return false;
  }
  const flee =
    unitHasRoleTag(c.u, "healer") || unitHasRoleTag(c.u, "support")
      ? combatTable().healerFleeBossDist
      : combatTable().dpsFleeBossDist;
  return dist(c.u.x, c.u.y, c.boss.x, c.boss.y) < flee;
}

/**
 * Spatial danger phenomenon: DangerZone under feet, any active floor
 * (leap/charge/brass/telegraph), and/or bossClose (ranged/healer).
 * Instinct delay gates reactions to this family ??not which reaction
 * (Role/gambit chooses that). Zones stay "active" after feet clear so
 * dangerReactAt is not reset and sticky keep-clear can fire without a fresh delay.
 */
function dangerPhenomenonActive(c: BrainCtx): boolean {
  const zones = collectRaidDangerZones(c.boss, {
    enemies: c.enemies,
    includeBoss: c.includeBoss,
  });
  if (unitInDangerZones(c.u, zones)) return true;
  // Any pending floor (leap/charge/brass/slam?? keeps phen live for keep-clear.
  if (zones.length > 0) return true;
  return isBossCloseCtx(c);
}

/**
 * Shared instinct awareness timer. Rising edge arms `dangerReactAt`;
 * inactive phenomenon clears it. Ready ??dodge + danger-reactive gambits
 * may score.
 * Soft??AA: feet already in painted charge Soft ??ready now (personal Soft
 * clear ??instinct delay). Painted charge Soft foresight halves delay.
 */
function dangerAwarenessReady(
  u: RaidUnit,
  active: boolean,
  time: number,
  ctx?: BrainCtx,
): boolean {
  if (!active) {
    u.brain.dangerReactAt = 0;
    return false;
  }
  let delay = instinctKnob(instinctOf(u), "reactionDelaySec");
  if (ctx) {
    const zones = collectRaidDangerZones(ctx.boss, {
      enemies: ctx.enemies,
      includeBoss: ctx.includeBoss,
    });
    if (chargeCorridorActive(zones)) {
      for (const z of zones) {
        if (
          z.shape === "line" &&
          z.skillId === "charge" &&
          pointInDangerZone(u.x, u.y, u.radius, z)
        ) {
          u.brain.dangerReactAt = time;
          return true;
        }
      }
      // Soft?AA Soft foresight: painted charge ? snap keep-clear (half delay).
      // Painted?ready-now Soft-regressed N20 0.45 ? keep half delay.
      delay = Math.min(delay, Math.max(0.02, delay * 0.5));
    }
  }
  if (u.brain.dangerReactAt <= 0) {
    u.brain.dangerReactAt = time + delay;
  }
  return time >= u.brain.dangerReactAt;
}

/** Gambit rows that wait on the same awareness timer as T0 dodge. */
function isDangerReactiveGambit(row: RaidGambitRow): boolean {
  return row[1] === "space";
}

/**
 * Instinct delay gates keep-clear *and* feet-in for **all** DangerZone colors
 * (red and yellow). Soft??AA charge Soft feet-in may skip delay via ctx.
 */
function dodgeAwarenessAllows(
  u: RaidUnit,
  phen: boolean,
  time: number,
  ctx?: BrainCtx,
): boolean {
  return dangerAwarenessReady(u, phen, time, ctx);
}

function decideDodgeIntent(c: BrainCtx, mul: number): AiIntent {
  const zones = collectRaidDangerZones(c.boss, {
    enemies: c.enemies,
    includeBoss: c.includeBoss,
  });
  // Feet in any paint ? no opportunity AA (swing plant fights escape travel).
  // Polar ???: abandon DPS for the whole dodge gambit (even feet-clear).
  const abandonFire = instinctAbandonFireWhileDodge(instinctOf(c.u));
  const fire =
    abandonFire || unitInDangerZones(c.u, zones)
      ? undefined
      : opportunityFire(c);
  const personal = dodgeGoalInBounds(
    c.u,
    c.boss,
    mul,
    c.enemies,
    undefined,
    c.includeBoss,
    c.allies,
  )!;
  const goal = blendDodgeGoalWithAdjutant(c, personal, mul);
  const intent = dodgeIntent(c.u, goal, c.boss, fire);
  // Red floor under feet ? heal silence. Yellow keep-clear / feet clear still
  // allow in-range heal so thrall chew during charge corridors can be rescued.
  // Polar ???: also silence heal while dodging (survival reflex).
  if (abandonFire || unitInDangerZones(c.u, zones, "red")) return intent;
  return withInRangeHeal(c, intent);
}

/**
 * Bond??Instinct composition (multiply willingness after instinct opens):
 * - Instinct already gated WHEN dodge opens (reactionDelaySec) and HOW FAR
 *   (dodgeDistMul / scaled red base).
 * - Bond (adjutantCompliance01) pulls the escape goal toward the adjutant seed,
 *   then re-projects clear of all floors so compliance cannot walk back into paint.
 * High bond + low instinct = willing but late/clumsy.
 * Low bond + high instinct = sharp personal dodge, ignore formation suggestion.
 */
function blendDodgeGoalWithAdjutant(
  c: BrainCtx,
  personal: { tx: number; ty: number },
  mul: number,
): { tx: number; ty: number } {
  // Polar ???: over-compliance pull (still Soft-projected clear of floors).
  const t = bondOverCompliancePull01(dispositionOf(c.u).bond);
  if (t < 0.05) return personal;
  const zones = collectRaidDangerZones(c.boss, {
    enemies: c.enemies,
    includeBoss: c.includeBoss,
  });
  // Feet-in escape keeps personal clear geometry (sidestep / land exit).
  // Bond pull applies to keep-clear / approach so compliance cannot collapse
  // an in-paint dodge back toward a formation seed in the cone.
  if (unitInDangerZones(c.u, zones)) return personal;
  const snap = planAdjutant({
    boss: c.boss,
    allies: c.allies,
    enemies: c.enemies,
    time: c.time,
    includeBoss: c.includeBoss,
    spawnHeat: c.spawnHeat ?? null,
  });
  const seed = slotFor(snap, c.u.id);
  if (!seed) return personal;
  const raw = {
    tx: personal.tx + (seed.x - personal.tx) * t,
    ty: personal.ty + (seed.y - personal.ty) * t,
  };
  const pad = Math.max(8, 24 * mul);
  const pushed = projectClearOfDangerZones(
    raw.tx,
    raw.ty,
    c.u.radius,
    zones,
    pad,
  );
  const out = resolveGoalInBounds(c.u, pushed.x, pushed.y);
  if (
    !pointClearsAllDangerZones(out.tx, out.ty, c.u.radius, zones, pad)
  ) {
    return personal;
  }
  return out;
}

// T0 ??reflex dodge:
// - Tank *open*: red anywhere, OR feet-in **brass** (yellow Role-hold was freezing
//   tanks in brass cones ??B seeded deaths). Yellow charge keep-clear / feet-in
//   charge stays red-gated (ironhorn sidestep Role ??SSOT).
// - DPS/healer: any zone (feet-in + keep-clear).
// Once open, goal XY pad-clears **all** active floors (SSOT: Role filter ??goal filter).
// Instinct delay gates ALL colors (feet-in + keep-clear). Bond blends path after open.
const ruleDodgeTank: RaidRule = {
  id: "dodge",
  score: (c) => {
    const phen = dangerPhenomenonActive(c);
    const zones = collectRaidDangerZones(c.boss, {
      enemies: c.enemies,
      includeBoss: c.includeBoss,
    });
    const redPresent = hasDangerTier(zones, "red");
    const brassZones = zones.filter((z) => z.skillId === "brass");
    const feetInBrass =
      brassZones.length > 0 && unitInDangerZones(c.u, brassZones);
    if (!redPresent && !feetInBrass) {
      dangerAwarenessReady(c.u, phen, c.time, c);
      return 0;
    }
    // Polar low (instinct?0): no AoE dodge attempt ? Soft?AA paths stay open.
    if (!instinctAllowsAoEDodge(instinctOf(c.u))) {
      dangerAwarenessReady(c.u, phen, c.time, c);
      return 0;
    }
    const mul = instinctScaledDodgeMul(c, redPresent);
    const goal = dodgeGoalInBounds(
      c.u,
      c.boss,
      mul,
      c.enemies,
      undefined,
      c.includeBoss,
      c.allies,
    );
    if (goal == null) {
      dangerAwarenessReady(c.u, phen, c.time, c);
      return 0;
    }
    if (!dodgeAwarenessAllows(c.u, phen, c.time, c)) return 0;
    return 100;
  },
  decide: (c) => {
    const zones = collectRaidDangerZones(c.boss, {
      enemies: c.enemies,
      includeBoss: c.includeBoss,
    });
    return decideDodgeIntent(
      c,
      instinctScaledDodgeMul(c, hasDangerTier(zones, "red")),
    );
  },
};

/**
 * Instinct owns dodge distance quality for yellow *and* red.
 * Red uses authored redDodgeMul scaled by (dodgeDistMul / mid) ??no speedMul.
 */
function instinctScaledDodgeMul(c: BrainCtx, redBase: boolean): number {
  const q = instinctKnob(instinctOf(c.u), "dodgeDistMul");
  if (!redBase) return q;
  const mid = instinctKnob(50, "dodgeDistMul");
  return combatTable().redDodgeMul * (q / Math.max(1e-6, mid));
}

function rangedDodgeMul(c: BrainCtx): number {
  const zones = collectRaidDangerZones(c.boss, {
    enemies: c.enemies,
    includeBoss: c.includeBoss,
  });
  return instinctScaledDodgeMul(c, hasDangerTier(zones, "red"));
}

/** Fight behaviors that low bond may refuse to abandon for SoftThreat *space* gambits. */
function isFightCommitBehavior(id: string | null | undefined): boolean {
  if (!id) return false;
  return (
    id === "engage" ||
    id === "survive" ||
    id === "tank_critical_fallback" ||
    id === "support"
  );
}

/**
 * Soft yield for SoftThreat *space* gambits only ??never DangerZone dodge.
 * While committed to a fight behavior, space may be blocked for a
 * bond-inverted slice of the commit window (personal sticky thickness).
 * Bond must not gate or zero dodge scores; instinct awareness is the only
 * delay that can defer danger reaction.
 *
 * Continuum (no stickyBonus?? cliff):
 * - bond 100 ??never blocks (remain > commitDur is impossible)
 * - bond 50 ??blocks while remain > 0.5?commitSec
 * - bond 0 ??blocks space for the entire commit window
 */
function bondBlocksSoftYield(c: BrainCtx): boolean {
  const sel = c.u.brain.select;
  if (!(c.time < sel.committedUntil)) return false;
  if (!isFightCommitBehavior(sel.activeId)) return false;
  const bond = dispositionOf(c.u).bond;
  const commitDur = commitSecForBond(bond);
  if (!(commitDur > 0)) return false;
  const remain = Math.max(0, sel.committedUntil - c.time);
  // bond0 personal: block whole window; bond100 collective: never block.
  return remain > commitDur * (bond / 100);
}

/**
 * While danger phen is active but instinct awareness is not ready yet, hold
 * still ??keep-clear *and* feet-in (red or yellow). Instinct owns reaction
 * latency for every floor color; freeze is the clumsy linger, not a red bypass.
 */
const ruleDangerFreeze: RaidRule = {
  id: "danger_freeze",
  score: (c) => {
    const phen = dangerPhenomenonActive(c);
    if (!phen) return 0;
    // Polar low: no upcoming AoE dodge ? do not linger-freeze for a reaction
    // that never opens (tank_hits_for_uptime ? fight in place).
    if (!instinctAllowsAoEDodge(instinctOf(c.u))) return 0;
    if (dangerAwarenessReady(c.u, phen, c.time, c)) return 0;
    const zones = collectRaidDangerZones(c.boss, {
      enemies: c.enemies,
      includeBoss: c.includeBoss,
    });
    const feetIn = unitInDangerZones(c.u, zones);
    // Feet in paint (any color): linger until instinct opens dodge.
    // Tank brass feet-in opens T0 after awareness; yellow charge Role-hold unchanged.
    if (feetIn) return 95;
    const dodgeMul = unitHasRoleTag(c.u, "tank")
      ? instinctScaledDodgeMul(c, true)
      : rangedDodgeMul(c);
    const freezeGoal =
      unitHasRoleTag(c.u, "tank")
        ? hasDangerTier(zones, "red") &&
          dodgeGoalInBounds(
            c.u,
            c.boss,
            dodgeMul,
            c.enemies,
            undefined,
            c.includeBoss,
            c.allies,
          ) != null
        : dodgeGoalInBounds(
            c.u,
            c.boss,
            dodgeMul,
            c.enemies,
            undefined,
            c.includeBoss,
            c.allies,
          ) != null;
    if (freezeGoal) {
      return 95;
    }
    return 0;
  },
  decide: (c) => {
    const fire = opportunityFire(c);
    const shootAdd = fire.attackTargetId != null && fire.wantAttack;
    const intent: AiIntent = {
      tx: c.u.x,
      ty: c.u.y,
      wantHealId: null,
      wantInterrupt: false,
      ...(shootAdd
        ? fire
        : { wantAttack: false, ...faceBoss(c.boss), attackTargetId: null }),
    };
    // Same heal contract as decideDodgeIntent: freeze holds feet during
    // instinct lag, but in-range triage still casts. Red feet silence.
    const zones = collectRaidDangerZones(c.boss, {
      enemies: c.enemies,
      includeBoss: c.includeBoss,
    });
    if (unitInDangerZones(c.u, zones, "red")) return intent;
    return withInRangeHeal(c, intent);
  },
};

const ruleDodgeRanged: RaidRule = {
  id: "dodge",
  score: (c) => {
    const phen = dangerPhenomenonActive(c);
    const zones = collectRaidDangerZones(c.boss, {
      enemies: c.enemies,
      includeBoss: c.includeBoss,
    });
    // Polar low (instinct?0): no AoE dodge attempt ? Soft?AA paths stay open.
    if (!instinctAllowsAoEDodge(instinctOf(c.u))) {
      dangerAwarenessReady(c.u, phen, c.time, c);
      return 0;
    }
    const mul = rangedDodgeMul(c);
    const goal = dodgeGoalInBounds(
      c.u,
      c.boss,
      mul,
      c.enemies,
      undefined,
      c.includeBoss,
      c.allies,
    );
    if (goal == null) {
      dangerAwarenessReady(c.u, phen, c.time, c);
      return 0;
    }
    // All colors (feet-in + keep-clear) wait on instinct ??no red bypass.
    if (!dodgeAwarenessAllows(c.u, phen, c.time, c)) return 0;
    // Bond soft-yield never mutes dodge ??instinct delay is the reaction gate;
    // bond blends the escape *path* after open (adjutant compliance).
    return 100;
  },
  decide: (c) => decideDodgeIntent(c, rangedDodgeMul(c)),
};

// T2 ??survive (dps/healer): retreat when critical / under fire while still
// in danger ??threat volumes OR a living lastAttacker (aggro), never a
// forced boss-distance gate.
function surviveScore(c: BrainCtx): number {
  const vols = buildLocalHostileVolumes(c.boss, c.time, c.u.radius, {
    enemies: c.enemies,
    includeBoss: c.includeBoss,
  });
  const volThreat = sampleThreatAt(c.u.x, c.u.y, vols);
  const lastId = c.u.hit.lastAttackerId;
  const attacker =
    lastId >= 0
      ? c.enemies.find((e) => e.id === lastId && e.hp > 0) ?? null
      : null;
  const underFire = underPressure(c.u.hit, c.time, 1.2);
  // Soft??AA: thrall chew is real pressure even when Soft sample is cold
  // (seed10: 3% HP dropped survive??engage at aa0 then thrall_aa finish).
  const thrallChew = !clearOfPackChewAttack(
    c.u.x,
    c.u.y,
    c.u.radius,
    c.enemies,
    2,
  );
  // Soft?AA: painted charge corridor under feet is Soft foresight pressure
  // (? packChew AA). Near-approach Soft foresight (thrallAA=0 charge|survive).
  const zones = collectRaidDangerZones(c.boss, {
    enemies: c.enemies,
    includeBoss: c.includeBoss,
  });
  let chargeFeet = false;
  let chargeNear = false;
  if (chargeCorridorActive(zones)) {
    for (const z of zones) {
      if (z.shape !== "line" || z.skillId !== "charge") continue;
      if (pointInDangerZone(c.u.x, c.u.y, c.u.radius, z)) {
        chargeFeet = true;
        break;
      }
      const aim = z.aimAngle ?? 0;
      const fx = Math.cos(aim);
      const fy = Math.sin(aim);
      const lx = -fy;
      const ly = fx;
      const dx = c.u.x - z.x;
      const dy = c.u.y - z.y;
      const along = dx * fx + dy * fy;
      const lat = Math.abs(dx * lx + dy * ly);
      const hw = Math.max(1, z.halfWidth ?? z.coneDeg ?? 20);
      const clearLat = hw + c.u.radius + 12;
      const len = Math.max(1e-3, z.range);
      const nearMul = encounterPackChargeForesightActive(
        c.boss,
        c.enemies,
        c.includeBoss,
      )
        ? 3.5
        : 2.4;
      if (lat < clearLat * nearMul && along > -28 && along < len + 48) {
        chargeNear = true;
      }
    }
  }
  // Spatially safe, not chased, and not thrall-chewed / charge Soft ??engage/kite.
  if (
    volThreat < raidSurviveThreatSafe() &&
    !(underFire && attacker) &&
    !thrallChew &&
    !chargeFeet &&
    !chargeNear
  ) {
    return 0;
  }

  const hpPct = c.u.maxHp > 0 ? c.u.hp / c.u.maxHp : 1;
  const d = dispositionOf(c.u);
  const thr = dispositionKnob(d, "surviveHpThr");
  // thr?? (grit 100): never enter survive / refuse to flee.
  if (!(thr > 0)) return 0;
  // Soft?AA: feet in thrall AA or charge Soft (feet/near) open survive.
  if (thrallChew || chargeFeet || chargeNear) {
    return 55;
  }
  // Below thr while Soft / under fire (thrall already handled above).
  if (
    hpPct < thr &&
    (volThreat >= raidSurviveThreatSafe() || underFire)
  ) {
    return 55;
  }
  return 0;
}

/**
 * Survive/retreat may outrank support, but a healer still in staff range of a
 * triage need must keep casting ? movement retreat + heal fire coexist.
 * Optional Layer-2 `healer_soft` / `kit_both`: Soft-HP in-staff only (Claim B banned).
 */
function withInRangeHeal(c: BrainCtx, intent: AiIntent): AiIntent {
  if (c.u.classId !== "healer") return intent;
  const pct = softAaHealBelowPct(classKnobs("healer").healLowestAllyBelowPct);
  const base = pickInRangeHealNeed(c.u, c.allies, pct, c.enemies);
  const need = resolveSoftSafeHealNeed(
    c.u,
    c.allies,
    c.enemies,
    pct,
    base,
    c.time,
  );
  if (!need) return intent;
  return {
    ...intent,
    wantAttack: false,
    wantHealId: need.id,
    ...facePoint(need.x, need.y),
    attackTargetId: null,
  };
}

const ruleSurviveRanged: RaidRule = {
  id: "survive",
  score: surviveScore,
  decide: (c) => {
    const retreat = retreatToAllyIntent(
      c.u,
      c.boss,
      c.allies,
      c.enemies,
      c.focusBoss,
      c.time,
      dispositionKnob(dispositionOf(c.u), "adjutantFollowMul"),
      c.includeBoss,
    );
    // Soft AA: while thralls chew, keep opportunity AA from retreat - do not
    // overwrite with in-range heal plant (healer thrall_aa during survive).
    // Rejected: pack-escape overwrite here -> Soft N20 0.65.
    if (!clearOfPackChewAttack(c.u.x, c.u.y, c.u.radius, c.enemies, 2)) {
      return retreat;
    }
    return withInRangeHeal(c, retreat);
  },
};


/** Thin RaidRule wrapper around a behavior kit. */
function ruleFromKit(
  ruleId: string,
  kitId: string,
  defaultScore = 1,
): RaidRule {
  return {
    id: ruleId,
    score: (c) => {
      const s = scoreFromKit(kitId, c, defaultKitPorts);
      // Kits without a score block (engage/support) use defaultScore.
      const kit = (raidCatalog.behaviorKits.kits as Record<string, { score?: unknown }>)[
        kitId
      ];
      if (kit?.score) return s;
      return defaultScore;
    },
    decide: (c) => decideFromKit(kitId, c, defaultKitPorts),
  };
}

/** True when add center is inside AA hit gate vs ally (matches Hit resolve). */
function addInMeleeOf(
  u: Pick<RaidUnit, "x" | "y" | "attackRange">,
  ally: Pick<RaidUnit, "x" | "y" | "radius">,
): boolean {
  return dist(u.x, u.y, ally.x, ally.y) < u.attackRange + ally.radius;
}

/**
 * Nearest living ally inside add AA. Body-sep / pack shove can park a thrall
 * against a non-sticky hero while sticky chase stays far ??still must swing.
 */
function nearestAllyInAddMelee(
  u: Pick<RaidUnit, "x" | "y" | "attackRange">,
  living: readonly RaidUnit[],
): RaidUnit | null {
  let best: RaidUnit | null = null;
  let bestD = Infinity;
  for (const a of living) {
    const d = dist(u.x, u.y, a.x, a.y);
    if (d >= u.attackRange + a.radius) continue;
    if (d < bestD) {
      bestD = d;
      best = a;
    }
  }
  return best;
}

/** Enemy add: chase highest-threat living ally (aggro table). */
const ruleEngageAdd: RaidRule = {
  id: "engage",
  score: () => 1,
  decide: (c) => {
    const { u, allies, time } = c;
    const living = allies.filter((a) => a.hp > 0);
    const sticky = topThreatTarget(u.aggro, living, {
      idOf: (a) => a.id,
      currentId: u.brain.targetId,
      sticky: raidAggroSticky(),
      time,
      bonusOf: (a) => {
        const d = dist(u.x, u.y, a.x, a.y);
        return Math.max(0, 50 - d * 0.1);
      },
    });
    if (!sticky) {
      return {
        tx: u.x,
        ty: u.y,
        wantAttack: false,
        wantHealId: null,
        wantInterrupt: false,
        faceX: u.x,
        faceY: u.y - 1,
        attackTargetId: null,
      };
    }
    u.brain.targetId = sticky.id;
    // Sticky in AA ??plant and hit. Else opportunity-fire whoever we are
    // body-adjacent to (collision/push parks thralls on the wrong face of the
    // pack while sticky remains deep / backline). Far sticky with no melee
    // contact ??chase only (backline shove silence is intentional).
    const stickyMelee = addInMeleeOf(u, sticky);
    const swing = stickyMelee ? sticky : nearestAllyInAddMelee(u, living);
    if (swing) {
      return {
        tx: u.x,
        ty: u.y,
        wantAttack: true,
        wantHealId: null,
        wantInterrupt: false,
        faceX: swing.x,
        faceY: swing.y,
        attackTargetId: swing.id,
      };
    }
    return {
      tx: sticky.x,
      ty: sticky.y,
      wantAttack: false,
      wantHealId: null,
      wantInterrupt: false,
      faceX: sticky.x,
      faceY: sticky.y,
      attackTargetId: sticky.id,
    };
  },
};

// ???? T1 order rules (commander tactic directives ??forced via forcedRuleId) ????

/**
 * Living team-1 already inside weapon Hit gate ??nearest then lower HP.
 * Used so dodge/freeze/forced-move can still AA thralls chewing on feet even
 * when sticky engage is boss / OOR charger (attackTargetId would otherwise
 * silence via dodgeIntent's add-only gate).
 */
function opportunityFire(c: BrainCtx): Pick<AiIntent, "wantAttack" | "faceX" | "faceY" | "attackTargetId"> {
  // Layer-2 Soft-safe focus among in-reach adds (no OOR chase).
  const reachAdds = c.enemies.filter((e) => {
    if (e.hp <= 0 || e.team !== 1) return false;
    return dist(c.u.x, c.u.y, e.x, e.y) < addAttackReach(c.u, e.radius);
  });
  const focusId = resolveSoftSafeFocusId(
    c.u,
    c.includeBoss ? c.boss : null,
    c.enemies,
    [
      ...reachAdds.map((e) => e.id),
      ...(c.includeBoss &&
      dist(c.u.x, c.u.y, c.boss.x, c.boss.y) <=
        hostileAttackReach(c.u, c.boss)
        ? [-1]
        : []),
    ],
    c.time,
  );
  if (focusId === -1 && c.includeBoss) {
    return {
      wantAttack: true,
      faceX: c.boss.x,
      faceY: c.boss.y,
      attackTargetId: null,
    };
  }
  if (focusId != null) {
    const t = c.enemies.find((e) => e.id === focusId);
    if (t) {
      return {
        wantAttack: true,
        faceX: t.x,
        faceY: t.y,
        attackTargetId: t.id,
      };
    }
  }
  // Prefer in-reach adds first (thrall_aa rescue during forced move).
  const inReach = pickInWeaponReachAdd(c.u, c.enemies);
  if (inReach) {
    return {
      wantAttack: true,
      faceX: inReach.x,
      faceY: inReach.y,
      attackTargetId: inReach.id,
    };
  }
  const hostile = pickEngageTarget(
    c.u,
    c.boss,
    c.enemies,
    c.u.classId,
    c.focusBoss,
    c.time,
    c.includeBoss,
    c.allies,
  );
  const d = dist(c.u.x, c.u.y, hostile.x, hostile.y);
  const reach = hostile.isBoss
    ? hostileAttackReach(c.u, c.boss)
    : addAttackReach(c.u, resolveAddRadius(c.enemies, hostile.id));
  return {
    wantAttack: hostile.isBoss ? d <= reach : d < reach,
    ...faceTarget(hostile),
  };
}

/**
 * Hard body-sep forbids entering the reach-capped minSep disk. A dash goal
 * inside that disk (or a straight path that only advances by walking into it)
 * never reaches arriveDist ??clear the order so engage can resume.
 * Far-side destinations (gestureAnchor / order past the obstacle) must NOT
 * clear when pressed on the near face ??path-around waypoints handle that.
 * gestureAnchor is intentionally left alone (order clear ??anchor clear).
 * Uses shared `dashArrivedOrHardBlocked` vs any radius hostile circle.
 */
function dashOrderArrivedOrBlocked(
  u: RaidUnit,
  hostile: { x: number; y: number; radius: number },
  tx: number,
  ty: number,
  arrive: number,
  finalDest?: { x: number; y: number } | null,
): boolean {
  if (dist(u.x, u.y, tx, ty) < arrive) return true;
  const minSep = hostileHardMinSep(u, hostile);
  return dashArrivedOrHardBlocked(
    u.x,
    u.y,
    tx,
    ty,
    hostile.x,
    hostile.y,
    minSep,
    arrive,
    finalDest,
  );
}

/** Gesture unit.dash ??sprint to order xy, no fire; clear on arriveDist (not a timer). */
const ruleUnitDash: RaidRule = {
  id: "unit.dash",
  score: (c) => (activeUnitOrder(c.u, c.time)?.kind === "dash" ? 1 : 0),
  decide: (c) => {
    const o = activeUnitOrder(c.u, c.time);
    let tx = o?.x ?? c.u.x;
    let ty = o?.y ?? c.u.y;
    const arrive =
      (raidCatalog.balance as { gesture?: { arriveDist?: number } }).gesture
        ?.arriveDist ?? 10;
    const ga = c.u.brain.gestureAnchor;
    const finalDest =
      ga && c.time < ga.until ? { x: ga.x, y: ga.y } : null;
    // Multi-leg dash: order xy is via waypoint; gestureAnchor holds final.
    if (
      o &&
      finalDest &&
      dist(c.u.x, c.u.y, tx, ty) < arrive &&
      dist(tx, ty, finalDest.x, finalDest.y) > arrive
    ) {
      o.x = finalDest.x;
      o.y = finalDest.y;
      tx = finalDest.x;
      ty = finalDest.y;
    } else if (
      dashOrderArrivedOrBlocked(c.u, c.boss, tx, ty, arrive, finalDest)
    ) {
      clearUnitOrder(c.u);
    }
    const g = resolveGoalInBounds(c.u, tx, ty);
    const mul =
      (raidCatalog.balance as { gesture?: { dashSpeedMul?: number } }).gesture
        ?.dashSpeedMul ?? 1.5;
    return {
      tx: g.tx,
      ty: g.ty,
      wantAttack: false,
      wantHealId: null,
      wantInterrupt: false,
      speedMul: mul,
      ...facePoint(tx, ty),
      attackTargetId: null,
    };
  },
};

/** Gesture unit.pin ??stand ground; healer heals in range; else opportunity fire. */
const ruleUnitPin: RaidRule = {
  id: "unit.pin",
  score: (c) => (activeUnitOrder(c.u, c.time)?.kind === "pin" ? 1 : 0),
  decide: (c) => {
    if (unitHasRoleTag(c.u, "healer")) {
      const pct = softAaHealBelowPct(classKnobs("healer").healLowestAllyBelowPct);
      const base = pickInRangeHealNeed(c.u, c.allies, pct, c.enemies);
      const need = resolveSoftSafeHealNeed(
        c.u,
        c.allies,
        c.enemies,
        pct,
        base,
        c.time,
      );
      if (need) {
        return {
          tx: c.u.x,
          ty: c.u.y,
          wantAttack: false,
          wantHealId: need.id,
          wantInterrupt: false,
          ...facePoint(need.x, need.y),
          attackTargetId: null,
        };
      }
    }
    return {
      tx: c.u.x,
      ty: c.u.y,
      wantHealId: null,
      wantInterrupt: false,
      ...opportunityFire(c),
    };
  },
};

function activeUnitOrder(
  u: RaidUnit,
  time: number,
): Order<RaidUnitOrderKind> | null {
  const o = u.brain.order.current;
  if (!o) return null;
  if (time >= o.until) {
    clearUnitOrder(u);
    return null;
  }
  return o;
}

const ruleOrderRegroup: RaidRule = {
  id: "order.regroup",
  score: (c) => (c.order?.kind === "regroup" ? 1 : 0),
  decide: (c) => {
    const slot = applySlot(c.order?.x ?? c.u.x, c.order?.y ?? c.u.y, c.u);
    const g = resolveGoalInBounds(c.u, slot.tx, slot.ty);
    return { tx: g.tx, ty: g.ty, wantHealId: null, wantInterrupt: false, ...opportunityFire(c) };
  },
};

const ruleOrderRetreat: RaidRule = {
  id: "order.retreat",
  score: (c) => (c.order?.kind === "retreat" ? 1 : 0),
  decide: (c) => {
    const slot = applySlot(c.order?.x ?? c.u.x, c.order?.y ?? c.u.y, c.u);
    const g = resolveGoalInBounds(c.u, slot.tx, slot.ty);
    // Forced withdrawal still kite-fires (never a helpless rout).
    return { tx: g.tx, ty: g.ty, wantHealId: null, wantInterrupt: false, ...opportunityFire(c) };
  },
};

const ruleOrderHold: RaidRule = {
  id: "order.hold",
  score: (c) => (c.order?.kind === "hold" ? 1 : 0),
  decide: (c) => ({
    tx: c.u.x,
    ty: c.u.y,
    wantHealId: null,
    wantInterrupt: false,
    ...opportunityFire(c),
  }),
};

const ORDER_RULES: readonly RaidRule[] = [
  ruleUnitDash,
  ruleUnitPin,
  ruleOrderRegroup,
  ruleOrderRetreat,
  ruleOrderHold,
];

// ???? gambit assembly (T3 = player-editable rule slots) ????????????????????????????????????????????

/** Built-in (non-kit) chip/tier2 rules referenced from classLoadouts. */
const BUILTIN_RULES: Record<string, RaidRule> = {
  survive_ranged: ruleSurviveRanged,
};

function resolveBehaviorRef(ruleId: string, ref: string): RaidRule {
  if (isBehaviorKitId(ref)) return ruleFromKit(ruleId, ref);
  const builtin = BUILTIN_RULES[ref];
  if (builtin) {
    return ruleId === builtin.id
      ? builtin
      : { id: ruleId, score: builtin.score, decide: builtin.decide };
  }
  throw new Error(`Unknown behavior ref: ${ref}`);
}

function chipRule(classId: RaidClassId, chipId: string): RaidRule | null {
  const ref = kitIdForChip(classId, chipId);
  if (!ref) return null;
  return resolveBehaviorRef(chipId, ref);
}

function conditionMet(chip: string, c: BrainCtx): boolean {
  const [id, argRaw] = chip.split(":");
  const arg = argRaw != null ? Number(argRaw) : NaN;
  switch (id) {
    case "always":
      return true;
    case "hpBelow":
      // Gambit chip: absolute HP% (Role/authored), not grit surviveHpThr.
      // See ???????? ?3.2 ??kit T2 uses surviveHpThr; gambit hpBelow stays abs.
      return c.u.hp / Math.max(1, c.u.maxHp) < (Number.isFinite(arg) ? arg : 0.5);
    case "allyHurt": {
      const pct = Number.isFinite(arg)
        ? arg
        : softAaHealBelowPct(classKnobs("healer").healLowestAllyBelowPct);
      return lowestAlly(c.allies, pct) != null;
    }
    case "addInReach":
      return c.enemies.some(
        (e) =>
          e.hp > 0 &&
          dist(c.u.x, c.u.y, e.x, e.y) < addAttackReach(c.u, e.radius),
      );
    case "bossClose": {
      return isBossCloseCtx(c);
    }
    default:
      return false;
  }
}

/** Wrap a behavior chip with its condition gate (score 0 when unmet). */
function gambitRule(row: RaidGambitRow, base: RaidRule, slot: number): RaidRule {
  const [cond] = row;
  const dangerReactive = isDangerReactiveGambit(row);
  const softSpace = row[1] === "space";
  return {
    id: base.id === "engage" || base.id === "support" ? base.id : `${base.id}`,
    score: (c) => {
      if (!conditionMet(cond, c)) return 0;
      if (dangerReactive) {
        const phen = dangerPhenomenonActive(c);
        if (!dangerAwarenessReady(c.u, phen, c.time, c)) return 0;
      }
      // Focus commit thickness resists abandoning DPS for soft space kite.
      if (softSpace && bondBlocksSoftYield(c)) return 0;
      return base.score(c);
    },
    decide: base.decide,
    ...(slot >= 0 ? {} : {}),
  };
}

/** Per-defId assembled rule list; rebuilt when the gambit rows ref changes. */
const gambitCache = new Map<
  string,
  { rows: readonly RaidGambitRow[]; rules: readonly RaidRule[] }
>();

function rulesFor(u: RaidUnit): readonly RaidRule[] {
  if (u.team === 1) return ADD_RULES;
  const rows = raidGambitRulesFor(u.defId, u.classId);
  const cached = gambitCache.get(u.defId);
  if (cached && cached.rows === rows) return cached.rules;

  const loadout = classLoadout(u.classId);
  const stack = loadout.ruleStack ?? {};
  const validFor = raidCatalog.gambits.chips.behaviors as Record<
    string,
    { validFor: readonly string[] }
  >;
  const maxSlots = raidCatalog.gambits.slots?.max ?? 6;
  const t3: RaidRule[] = [];
  for (const row of rows.slice(0, maxSlots)) {
    const behaviorId = row[1];
    const base = chipRule(u.classId, behaviorId);
    const valid = validFor[behaviorId]?.validFor?.includes(u.classId);
    if (!base || !valid) continue; // invalid equip rows are inert (validated in data-harness)
    t3.push(gambitRule(row, base, t3.length));
  }

  const rules: RaidRule[] = [];
  rules.push(stack.dodge === "tank" ? ruleDodgeTank : ruleDodgeRanged);
  rules.push(ruleDangerFreeze);
  if (stack.orders !== false) rules.push(...ORDER_RULES);
  if (stack.tier2) {
    rules.push(resolveBehaviorRef("survive", stack.tier2));
  }
  rules.push(...t3);
  const fallbackRef = loadout.fallback;
  if (!fallbackRef) throw new Error(`classLoadout ${u.classId} missing fallback`);
  rules.push(resolveBehaviorRef("engage", fallbackRef));

  gambitCache.set(u.defId, { rows, rules });
  return rules;
}

const ADD_RULES: readonly RaidRule[] = [ruleEngageAdd];

function selectKnobsFor(u: RaidUnit) {
  return { commitSec: commitSecForBond(dispositionOf(u).bond) };
}

/** Idle fallback ??never emitted in practice (engage rules always apply). */
function idleIntent(u: RaidUnit): AiIntent {
  return {
    tx: u.x,
    ty: u.y,
    wantAttack: false,
    wantHealId: null,
    wantInterrupt: false,
    faceX: u.aimX,
    faceY: u.aimY,
    attackTargetId: null,
  };
}

export function computeAiIntent(
  u: RaidUnit,
  boss: RaidBoss,
  allies: readonly RaidUnit[],
  enemies: readonly RaidUnit[],
  focusTankId: number | null,
  time: number,
  forcedRuleId: string | null = null,
  order: Order<RaidOrderKind> | null = null,
  includeBoss = true,
  spawnHeat: SpawnHeatState | null = null,
): AiIntent {
  const ctx: BrainCtx = {
    u,
    boss,
    allies,
    enemies,
    focusBoss: focusTankId != null,
    time,
    includeBoss,
    order,
    spawnHeat,
  };
  // Lethal-danger supremacy: commander forced orders never suppress a red dodge
  // (no suicide orders). Unit gesture orders (dash/pin) are user-will ??keep them.
  const isUnitOrder =
    forcedRuleId === "unit.dash" || forcedRuleId === "unit.pin";
  if (forcedRuleId && !isUnitOrder) {
    const zones = collectRaidDangerZones(boss, {
      enemies,
      includeBoss,
    });
    if (
      instinctAllowsAoEDodge(instinctOf(u)) &&
      hasDangerTier(zones, "red") &&
      dodgeGoalInBounds(
        u,
        boss,
        combatTable().redDodgeMul,
        enemies,
        undefined,
        includeBoss,
        allies,
      ) != null
    ) {
      forcedRuleId = null; // dodge tier wins this evaluation
    }
  }
  const picked = selectBehavior(
    rulesFor(u),
    ctx,
    u.brain.select,
    time,
    selectKnobsFor(u),
    forcedRuleId,
  );
  const activeId = u.brain.select.activeId;
  if (activeId === "danger_freeze") {
    const zones = collectRaidDangerZones(boss, {
      enemies,
      includeBoss,
    });
    const inZones = zones.filter((z) => unitInDangerZones(u, [z]));
    raidFreezeDiag.noteBehaviorHold(u.id, activeId, time, {
      classId: u.classId,
      inZoneShapes: inZones.map((z) => z.shape),
      inZoneIds: inZones.map((z) => z.id),
      dangerReactAt: u.brain.dangerReactAt,
    });
  } else {
    raidFreezeDiag.noteBehaviorHold(u.id, activeId, time);
  }
  const intent = picked?.decision ?? idleIntent(u);
  // User dash may cut through floors. Freeze must hold feet (finalize would
  // push out of a zone and invert the awareness-delay contract).
  if (activeId === "unit.dash" || activeId === "danger_freeze") return intent;
  const zones = collectRaidDangerZones(boss, { enemies, includeBoss });
  if (zones.length === 0) return intent;
  // Polar low (instinct ?20): no AoE dodge / finalize escape ? stay and fight
  // if already painted; otherwise do not keep-clear-nudge (looks like dodge).
  if (!instinctAllowsAoEDodge(instinctOf(u))) {
    if (unitInDangerZones(u, zones)) {
      return { ...intent, tx: u.x, ty: u.y };
    }
    return intent;
  }
  const mul = instinctKnob(instinctOf(u), "dodgeDistMul");
  const g = finalizeAllyMoveGoal(u, intent.tx, intent.ty, zones, mul);
  return { ...intent, tx: g.tx, ty: g.ty };
}

